import React, { useRef } from 'react';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { NavLink } from '@shared/core';
import { animationTransition } from '@shared/utils/animationTransition';
import { useCardTelemetry } from '@apps/card/Card.telemetry';
import { pxToRem } from '@withjoy/joykit/theme';
import classNames from 'classnames';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useOuterClick } from '@shared/utils/hooks/useOuterClick';
import type { CategoryNavItem, NavItemType, SubcategoryChild, SubcategoryNavItem } from './useNavItems';
import { useNavItems } from './useNavItems';

const CategoryLink = ({ item }: { item: CategoryNavItem }) => {
  const { viewCategoryDesigns } = useCardTelemetry();
  return (
    <Box
      position="relative"
      display="inline-flex"
      alignItems="center"
      typographyVariant={{ md: 'hed2' }}
      as={NavLink}
      to={item.to}
      isActive={item.isActive}
      onClick={() => {
        viewCategoryDesigns(item.category);
      }}
      cursor="pointer"
      paddingY={4}
      data-category={item.category}
      _after={{
        content: '" "',
        position: 'absolute',
        bottom: [0, null, null, pxToRem(11)],
        width: '100%',
        height: ['2px', null, null, '1px'],
        backgroundColor: 'mono12',
        opacity: 0,
        transition: animationTransition('opacity'),
      }}
      __css={{
        ['&.active']: {
          _after: {
            opacity: 1,
          },
          cursor: 'default',
          pointerEvents: 'none',
        },
        ['&:not(.active)']: {
          _hover: {
            _after: {
              opacity: 0.2,
            },
          },
        },
      }}
    >
      {item.title}
    </Box>
  );
};

const SubcategoryLink = ({ item, onToggleMenu }: { item: SubcategoryNavItem; onToggleMenu: () => void }) => {
  const { viewCategoryDesigns } = useCardTelemetry();

  const navItemRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <Box
        position="relative"
        display="inline-flex"
        alignItems="center"
        typographyVariant={{ md: 'hed2' }}
        onClick={e => {
          viewCategoryDesigns(item.category);
          onToggleMenu();
        }}
        cursor="pointer"
        paddingY={4}
        className={classNames({
          active: item.isActive(),
        })}
        data-category={item.category}
        data-type="subcategory"
        _after={{
          content: '" "',
          position: 'absolute',
          bottom: [0, null, null, pxToRem(11)],
          width: '100%',
          height: ['2px', null, null, '1px'],
          backgroundColor: 'mono12',
          opacity: 0,
          transition: animationTransition('opacity'),
        }}
        __css={{
          ['&.active']: {
            _after: {
              opacity: 1,
            },
          },
          ['&:not(.active)']: {
            _hover: {
              _after: {
                opacity: 0.2,
              },
            },
          },
        }}
        ref={navItemRef}
      >
        {item.title}
      </Box>
    </>
  );
};

const SubcategoryMenu = ({ item, isExpanded, onToggleMenu }: { item: SubcategoryNavItem; isExpanded: boolean; onToggleMenu: () => void }) => {
  const { viewCategoryDesigns } = useCardTelemetry();
  const menuRef = useOuterClick(e => {
    const target = e.target as HTMLElement;
    if (!!target && target.dataset && target.dataset.category === item.category && target.dataset.type === 'subcategory') {
      // The corresponding nav item was clicked -- don't close the menu.
      return;
    }

    if (isExpanded) {
      onToggleMenu();
    }
  });

  const handleOnChildClick = useEventCallback((child: SubcategoryChild) => {
    viewCategoryDesigns(child.category);
    onToggleMenu();
  });

  return (
    <Box
      __css={{
        position: 'absolute',
        top: 'calc(100% + 1px)',
        left: 0,
        zIndex: 'dropdown',
        width: '100%',
        backgroundColor: 'white',
        boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.07), 0px 7px 27px 0px rgba(44, 41, 37, 0.06)',
        height: (isExpanded ? (menuRef.current?.scrollHeight ?? -2) : -2) + 2,
        opacity: isExpanded ? 1 : 0,
        pointerEvents: isExpanded ? 'auto' : 'none',
        overflow: 'hidden',
        transform: `translate3d(0, 0, 0)`,
      }}
      transition="height 0.4s cubic-bezier(0.23, 1, 0.32, 1), opacity 0.4s cubic-bezier(0.38, 0.41, 0.27, 1) 0.3s"
      ref={menuRef}
    >
      <Flex
        as="ul"
        justifyContent="center"
        paddingY={{ _: 5, sm2: 7 }}
        columnGap={4}
      >
        {item.children.map((child, idx) => {
          return (
            <Box
              key={idx}
              as="li"
            >
              <Flex
                as={NavLink}
                to={child.to}
                isActive={child.isActive}
                onClick={() => handleOnChildClick(child)}
                flexDirection="column"
                alignItems="center"
                rowGap={5}
              >
                <Box
                  __css={{
                    borderRadius: 3,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    width: [108, null, null, 140],
                    height: [117, null, null, 172],
                  }}
                  style={{ backgroundImage: `url('${child.thumbnailUrl}')` }}
                />

                <TextV2>{child.title}</TextV2>
              </Flex>
            </Box>
          );
        })}
      </Flex>
    </Box>
  );
};

const NavItem = ({ item, idx, onExpandClick }: { item: NavItemType; idx: number; onExpandClick: (idx: number) => void }) => {
  const wrappedOnExpandSubcategoryClick = useEventCallback(() => {
    onExpandClick(idx);
  });
  return item.type === 'category' ? (
    <CategoryLink item={item} />
  ) : (
    <SubcategoryLink
      item={item}
      onToggleMenu={wrappedOnExpandSubcategoryClick}
    />
  );
};

export const CategoryNav = () => {
  const { navItems, expandedCategoryIndex, onExpandCategoryClick } = useNavItems();

  return (
    <Box position="relative">
      <Flex
        as="ul"
        justifyContent={{ _: 'flex-start', md: 'center' }}
        height={10}
        whiteSpace="nowrap"
        overflowX="scroll"
        paddingX={6}
        borderBottom={'1px solid'}
        borderBottomColor={'mono3'}
        columnGap={6}
        position="relative"
        style={{
          scrollbarWidth: 'none',
        }}
      >
        {navItems.map((item, index) => {
          return (
            <NavItem
              key={index}
              idx={index}
              onExpandClick={onExpandCategoryClick}
              item={item}
            />
          );
        })}
      </Flex>

      {navItems.map((item, idx) => {
        if (item.type === 'subcategory') {
          return (
            <SubcategoryMenu
              key={idx}
              item={item}
              isExpanded={idx === expandedCategoryIndex}
              onToggleMenu={() => onExpandCategoryClick(idx)}
            />
          );
        }
        return null;
      })}
    </Box>
  );
};
